import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { concat, forkJoin, interval, Observable, of, throwError, timer } from 'rxjs';
import { catchError, delay, ignoreElements, map, take } from 'rxjs/operators';
import { StreamStatus, UploadFileInfo } from '../common/api.types';
import {
  AnalysisOverview, ContrastFindings,
  IngestionSummary, MatrixOverview, MatrixOverviewQuery, MatrixPreview, OmicsChart,
  OmicsEdaVariable, OmicsRunConfig, OmicsService, OmicsSession, ProcessingSummary,
  StrategyConfirm, StrategyReview,
} from './omics.service';
import { DemoFailures } from '../common/demo-failures';
import { fakeStream } from '../common/sse';
import { DEMO_DELAY, SSE_MULT } from '../../constants/demo.config';

/** Demo implementation — reads static JSON from assets/demo/omics/ and fakes
 *  the SSE streams with RxJS. Timing and failure switches come from
 *  shared/constants/demo.config.ts, shared with the tabular demo. */
@Injectable()
export class OmicsDemoService extends OmicsService {
  private readonly http = inject(HttpClient);
  private readonly failures = inject(DemoFailures);

  override setRunName(_name: string): void {}

  listFiles(): Observable<UploadFileInfo[]> {
    if (this.failures.should('omicsListFiles')) {
      return throwError(() => ({ message: 'Simulated error: could not load file list' }));
    }
    return this.http.get<{ files: UploadFileInfo[] }>('assets/demo/omics/upload_files.json')
      .pipe(delay(DEMO_DELAY), map(res => res.files));
  }

  // The messages are the ones docs/omics/OMICS_API_ENDPOINTS.md documents for
  // the ingestion SSE stream, verbatim — past tense included, which the doc
  // says is deliberate (the graph emits a node's event AFTER it finishes).
  startIngestion(_config: OmicsRunConfig): Observable<StreamStatus> {
    if (this.failures.should('omicsIngestion')) {
      return throwError(() => ({ message: 'Simulated error: omics ingestion failed' }));
    }
    const messages = [
      'Starting the omics pipeline…',
      'Harmonized sample metadata.',
      'Classified input files by omics modality.',
      'Curated inputs and checked the sample sheet.',
    ];
    return concat(
      interval(1400 * SSE_MULT).pipe(take(messages.length), map(i => ({ message: messages[i] }))),
      timer(900 * SSE_MULT).pipe(ignoreElements()),
    );
  }

  reconnectIngestion(config: OmicsRunConfig): Observable<StreamStatus> {
    return this.startIngestion(config);
  }

  getIngestionSummary(): Observable<IngestionSummary> {
    if (this.failures.should('omicsIngestionSummary')) {
      return throwError(() => ({ message: 'Simulated error: could not load the ingestion summary' }));
    }
    return this.http.get<{ summary: IngestionSummary }>('assets/demo/omics/ingestion_summary.json')
      .pipe(delay(DEMO_DELAY), map(res => res.summary));
  }

  getSessions(): Observable<OmicsSession[]> {
    if (this.failures.should('omicsSessions')) {
      return throwError(() => ({ message: 'Simulated error: could not load run history' }));
    }
    return this.http.get<{ sessions: OmicsSession[] }>('assets/demo/omics/sessions.json')
      .pipe(delay(DEMO_DELAY), map(res => res.sessions));
  }

  // Past tense on purpose — the graph yields a node's event AFTER it finishes, so a
  // present-participle message would claim work is under way that has just ended.
  // The stream ends at the review gate, which is what the doc's terminal event says.
  private readonly strategyMessages = [
    'Harmonized sample metadata and resolved the target column.',
    'Profiled the cohort (class balance, confounding, intensities).',
    'Rendered the EDA charts for the selected variables.',
    'Strategist proposed normalization and filtering parameters.',
    'Parameters ready for review.',
  ];

  startStrategy(): Observable<StreamStatus> {
    if (this.failures.should('omicsStrategy')) return throwError(() => ({ message: 'Simulated error: strategy stage failed' }));
    return fakeStream(this.strategyMessages, 1100 * SSE_MULT, 900 * SSE_MULT);
  }

  reconnectStrategy(): Observable<StreamStatus> { return this.startStrategy(); }

  getStrategyReview(): Observable<StrategyReview> {
    if (this.failures.should('omicsStrategyReview')) {
      return throwError(() => ({ message: 'Simulated error: could not load the review' }));
    }
    return this.http.get<{ review: StrategyReview }>('assets/demo/omics/strategy_review.json')
      .pipe(delay(DEMO_DELAY), map(res => res.review));
  }

  // The real chart layer reads result files and writes JSON — no R, no idat, no LLM — so it
  // is fast enough to re-run on demand. Demo mirrors that: it serves the payloads the
  // pipeline actually wrote for a ROSMAP methylation run, one file per panel, and returns
  // only the ones that are switched on. A panel with no fixture is simply absent, the same
  // way the real layer skips a column whose inputs are not on disk.
  //
  // Two of the stage's panels are not per-column — the class balance and the confounding
  // table describe the cohort itself — so they are always rendered, after whatever the
  // reviewer asked for.
  private readonly COHORT_CHARTS = ['class_distribution', 'design_confounding'];

  rebuildCharts(variables: OmicsEdaVariable[]): Observable<OmicsChart[]> {
    const ids = [
      ...variables.filter(v => v.plot).map(v => `${v.column}_distribution`),
      ...this.COHORT_CHARTS,
    ];
    return forkJoin(
      ids.map(id => this.http
        // Foldered by the pipeline's own stage, the one each payload declares in its
        // `stage` field — the same names its output directory uses. Flat, a reader could
        // not tell which screen a chart belongs to.
        .get<OmicsChart>(`assets/demo/omics/charts/01_eda/${id}.json`)
        .pipe(catchError(() => of(null)))),
    ).pipe(
      delay(DEMO_DELAY),
      map(charts => charts.filter((c): c is OmicsChart => !!c)),
    );
  }

  private readonly processingMessages = [
    'Confirmed the cohort and the analysis plan.',
    'Read the idat files and built the intensity matrix.',
    'Applied noob normalization and the QC filters.',
    'Fitted the differential model.',
  ];

  startProcessing(_review: StrategyConfirm): Observable<StreamStatus> {
    if (this.failures.should('omicsProcessing')) {
      return throwError(() => ({ message: 'Simulated error: processing failed to start' }));
    }
    return fakeStream(this.processingMessages, 1200 * SSE_MULT, 900 * SSE_MULT);
  }

  reconnectProcessing(): Observable<StreamStatus> { return this.startProcessing({} as StrategyConfirm); }

  /** The messages `analysis/status` is documented to emit, in the order the graph produces
   *  them: the analyzer runs, then it may loop back through batch adjustment and the
   *  literature review before finishing. The two middle ones are the interesting half —
   *  a reverted adjustment and an underpowered contrast are things the reader has to know
   *  happened, and this loader is where they happen. */
  private readonly analysisMessages = [
    'Ranked the differential features per contrast.',
    'Differential analysis complete.',
    'Batch adjustment (design) reverted: calibration worsened',
    'Warning (underpowered_contrast): mci_vs_nci n=(12,264)',
    'Searched the literature for the top markers.',
    'Literature review complete.',
    'Rendered the charts for every stage.',
  ];

  startAnalysis(): Observable<StreamStatus> {
    if (this.failures.should('omicsAnalysis')) {
      return throwError(() => ({ message: 'Simulated error: analysis failed to start' }));
    }
    return fakeStream(this.analysisMessages, 1200 * SSE_MULT, 900 * SSE_MULT);
  }

  reconnectAnalysis(): Observable<StreamStatus> { return this.startAnalysis(); }

  /** The four contrasts of the ROSMAP run, with their real lambdas, group sizes and hit
   *  counts as `qc_genomic_inflation.csv` recorded them. */
  getAnalysisOverview(): Observable<AnalysisOverview> {
    if (this.failures.should('omicsAnalysisOverview')) {
      return throwError(() => ({ message: 'Simulated error: could not load the analysis summary' }));
    }
    return this.http.get<AnalysisOverview>('assets/demo/omics/analysis/overview.json')
      .pipe(delay(DEMO_DELAY));
  }

  /** One file per contrast, mirroring the per-contrast endpoints: the screen asks for the
   *  one it is showing, not for all of them. mci+_vs_nci is in there deliberately — it is
   *  the underpowered one, and the only way to see the "not computed, and here is why"
   *  state without editing a fixture. */
  getContrastFindings(contrastId: string): Observable<ContrastFindings> {
    if (this.failures.should('omicsContrastFindings')) {
      return throwError(() => ({ message: 'Simulated error: could not load this contrast' }));
    }
    // The ids carry a "+" (`mci+_vs_nci`) and this is a path segment, where some servers
    // read it as a space. Encoded it round-trips to the real filename either way.
    return this.http.get<ContrastFindings>(
      `assets/demo/omics/analysis/${encodeURIComponent(contrastId)}.json`)
      .pipe(delay(DEMO_DELAY));
  }

  getAnalysisChart(chartId: string): Observable<OmicsChart> {
    return this.http.get<OmicsChart>(
      `assets/demo/omics/charts/05_differential/${encodeURIComponent(chartId)}.json`)
      .pipe(delay(DEMO_DELAY));
  }

  getProcessingSummary(): Observable<ProcessingSummary> {
    if (this.failures.should('omicsProcessingSummary')) {
      return throwError(() => ({ message: 'Simulated error: could not load the processing summary' }));
    }
    return this.http.get<ProcessingSummary>('assets/demo/omics/processing_summary.json')
      .pipe(delay(DEMO_DELAY));
  }

  getMatrixPreview(): Observable<MatrixPreview> {
    if (this.failures.should('omicsMatrixPreview')) {
      return throwError(() => ({ message: 'Simulated error: could not load the matrix preview' }));
    }
    return this.http.get<MatrixPreview>('assets/demo/omics/matrix_preview.json')
      .pipe(delay(DEMO_DELAY));
  }

  /** Generated rather than served from a file: 900 x 736 bytes is 662 KB, and a fixture that
   *  size would sit in the repo forever to say something a formula says in twenty lines. The
   *  numbers are deterministic and depend on the ABSOLUTE probe index, so zooming reveals
   *  finer structure that is always the same — which is what the real endpoint will do by
   *  binning a narrower slice. */
  getMatrixOverview(q: MatrixOverviewQuery = {}): Observable<MatrixOverview> {
    if (this.failures.should('omicsMatrixOverview')) {
      return throwError(() => ({ message: 'Simulated error: could not load the matrix overview' }));
    }
    const TOTAL_PROBES = 425930, TOTAL_SAMPLES = 736;
    const p0 = Math.max(0, q.probe_from ?? 0);
    const p1 = Math.min(TOTAL_PROBES, q.probe_to ?? TOTAL_PROBES);
    const s0 = Math.max(0, q.sample_from ?? 0);
    const s1 = Math.min(TOTAL_SAMPLES, q.sample_to ?? TOTAL_SAMPLES);

    const span = Math.max(1, p1 - p0);
    const cols = Math.max(1, s1 - s0);
    const rows = Math.min(q.rows ?? 900, span);

    const values = new Uint8Array(rows * cols);
    for (let y = 0; y < rows; y++) {
      const p = p0 + Math.floor((y + 0.5) * span / rows);
      for (let x = 0; x < cols; x++) {
        values[y * cols + x] = Math.round(demoBeta(p, s0 + x) * 255);
      }
    }

    // Named only when a band is one probe: a band covering hundreds cannot honestly carry
    // the id of any single one of them.
    const probe_ids = span <= rows
      ? Array.from({ length: rows }, (_, y) => demoProbeId(p0 + Math.floor((y + 0.5) * span / rows)))
      : undefined;

    return of({ rows, cols, values, min: 0, max: 1, probe_from: p0, probe_to: p1, probe_ids })
      .pipe(delay(DEMO_DELAY));
  }
}

/* ── The stand-in for the pipeline ────────────────────────────────────────────
   Methylation is bimodal: a probe is either almost fully methylated or almost not, with a
   minority in between. Two stretches behave differently from the rest and a handful of
   samples sit off the others — which is exactly what the overview exists to reveal. */

function demoHash(n: number): number {
  n = (n ^ 61) ^ (n >>> 16);
  n = n + (n << 3);
  n = n ^ (n >>> 4);
  n = Math.imul(n, 0x27d4eb2d);
  n = n ^ (n >>> 15);
  return (n >>> 0) / 4294967296;
}

function demoBeta(probe: number, sample: number): number {
  const r = demoHash(probe), j = demoHash(probe * 3 + 1);
  let base = r < 0.42 ? 0.06 + j * 0.10
           : r < 0.84 ? 0.84 + j * 0.12
           :            0.35 + j * 0.30;
  const f = probe / 425930;
  if (f > 0.300 && f < 0.345) base = 0.55 + j * 0.12;
  if (f > 0.710 && f < 0.735) base = 0.16 + j * 0.10;

  let off = ((sample * 2654435761) % 1000 / 1000 - 0.5) * 0.05;
  if (sample > 604 && sample < 617) off += 0.20;
  if (sample > 281 && sample < 287) off -= 0.16;

  const v = base + off + (demoHash(probe * 7919 + sample) - 0.5) * 0.06;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function demoProbeId(probe: number): string {
  return 'cg' + String(Math.floor(demoHash(probe * 31 + 7) * 1e8)).padStart(8, '0');
}
